"use client";

import { useState } from "react";
import { Send, AlertCircle } from "lucide-react";
import { useCommsPanel } from "../CommsProvider";

interface EmailComposerProps {
  onSent: () => void;
}

export function EmailComposer({ onSent }: EmailComposerProps) {
  const { activeLeadId } = useCommsPanel();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSend() {
    if (!body.trim() || !activeLeadId || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`/api/leads/${activeLeadId}/timeline/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: body.trim(), subject: subject.trim() || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: "Failed to send email" } }));
        setError(err.error?.message ?? `Failed to send email (${res.status})`);
        return;
      }
      setSubject("");
      setBody("");
      onSent();
    } catch {
      setError("Network error — could not send email");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="border-t border-gray-200 bg-white px-3 py-3">
      <p className="mb-2 text-xs font-medium text-indigo-700">Send Email</p>
      {error && (
        <div className="mb-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-2 py-1.5">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-red-500" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        placeholder="Subject (optional)"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-none focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        placeholder="Email body..."
        onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleSend(); }}
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[10px] text-gray-400">Ctrl+Enter to send</span>
        <button
          onClick={handleSend}
          disabled={!body.trim() || sending}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          {sending ? "Sending..." : "Send Email"}
        </button>
      </div>
    </div>
  );
}
